import React, { FC, FormEvent, useEffect, useMemo, useRef, useState } from "react"
import { graphql } from "gatsby"
import xss from "xss"

import { HubspotFormFragmentFragment, HubspotFormInputFragmentFragment } from "@graphqlTypes"
import { generateConditionalClassName, isClientSide } from "@utils"

import { submitFormData } from "./HubspotForm.utils"

import Field, { FieldElements } from "./Field/Field"
import LegalConsentCheckbox from "./LegalConsentCheckbox/LegalConsentCheckbox"
import SubmissionMessage from "./SubmissionMessage/SubmissionMessage"
import HiddenField from "./HiddenField/HiddenField"
import FileUploadField from "./FileUploadField/FileUploadField"

import "./HubspotForm.scss"

export type HubspotFormProps = {
  isCompact?: boolean,
  onSubmit?: (formEl: HTMLFormElement) => void,
  onSubmitted?: (formEl: HTMLFormElement) => void,
  redirectUrl?: string | null,
  customSuccessMessage?: string,
}

const TRIAL_FORM_ID = "c6177e6a-a687-467a-b2cf-dd8a1daa66f9"

const DEFAULT_SUCCESS_MESSAGE = "Thank you!"

const HAS_MANY_FIELDS_THRESHOLD = 6

enum FIELD_TYPES {
  dropdown = "dropdown",
  email = "email",
  file = "file",
  multi_line_text = "multi_line_text",
  radio = "radio",
  single_line_text = "single_line_text",
}

const HubspotFormSubmitButton: FC<{
  buttonText?: string | null,
  isCompact?: boolean
}> = ({
  buttonText,
  isCompact,
}) => (
    <button
      className={`button ${isCompact ? "button--M" : "button--L"} submit-button`}
      type="submit"
      name="submit"
    >
      {buttonText || "Submit"}
    </button>
  )

type FormInputOrRichText = HubspotFormInputFragmentFragment & { richText?: string }

const HubspotForm: FC<HubspotFormFragmentFragment & HubspotFormProps> = ({
  configuration,
  alternative_id,
  displayOptions,
  fieldGroups,
  isCompact,
  legalConsentOptions,
  onSubmit,
  onSubmitted,
  redirectUrl,
  customSuccessMessage,
}) => {
  if (!alternative_id || !fieldGroups || fieldGroups.length === 0) return null

  const formRef = useRef<HTMLFormElement | null>(null)

  // If a field.name is invalid, set to `true`
  const [inputValidationMap, setInputValidationMap] = useState<{ [name: string]: boolean }>({})

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [successMessage, setSuccessMessage] = useState<string | undefined>()
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const isSubmitted: boolean = !!(successMessage || errorMessage)

  if (!fieldGroups) return null

  const fields: Array<FormInputOrRichText> = useMemo(() => {
    return fieldGroups.reduce<any[]>((acc, fieldGroup) => {
      const rawFields = fieldGroup?.alternative_fields?.filter(f => !!f) || []
      const mappedFields: FormInputOrRichText[] = rawFields.map(field => ({
        ...field,
        // NOTE: Required because the newest forms coming from the Hubspot API
        //       do no correctly set the `hidden` boolean field :'(
        hidden: field.hidden || !!field.name?.match(/utm_|hs_|gclid|content_types/)
      }))
      if (fieldGroup?.richText) {
        mappedFields.push({
          name: "richText",
          fieldType: "richText",
          richText: fieldGroup.richText,
        })
      }
      return [
        ...acc,
        ...mappedFields
      ]
    }, [])
  }, [fieldGroups])

  if (!fields) return null

  const isEmailCatcher: boolean = useMemo(() => {
    const hasEmailField = fields.some(field => field?.name === "email")

    const hasMultipleInputs: boolean = fields.filter(field =>
      field.fieldType !== "email" &&
      field.fieldType !== "file" &&
      !field.hidden
    ).length > 0

    return hasEmailField && !hasMultipleInputs
  }, [fields])

  const hasManyFields: boolean = useMemo(() => {
    return fields.filter(field => !field.hidden && field.fieldType !== "richText").length >= HAS_MANY_FIELDS_THRESHOLD
  }, [fieldGroups])

  const useMediumInputs = !!isCompact || hasManyFields

  // Set the form to `novalidate` so that we can implement our own validation checking
  useEffect(() => {
    if (!isClientSide() || !formRef.current) return
    formRef.current.noValidate = true
  }, [])

  // Validate an individual field
  const validateField = (el: FieldElements) => {
    setInputValidationMap(_ivm => ({
      ..._ivm,
      [el.name]: !el.checkValidity()
    }))
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!formRef.current) return

    // If the form is invalid, check the validity of each field and put these validations in the inputValidationMap
    if (!formRef.current.checkValidity()) {
      const inputs = formRef.current.querySelectorAll<FieldElements>(
        "input:not([hidden]), textarea:not([hidden]), select:not([hidden]), input.fileUploadField"
      )
      const _ivm = Array.from(inputs).reduce((acc, input) => ({ ...acc, [input.name]: !input.checkValidity() }), {})
      setInputValidationMap(_ivm)
      inputs[0].focus()
      return
    }

    if (onSubmit) onSubmit(formRef.current)

    setIsSubmitting(true)

    try {
      await submitFormData(formRef.current, alternative_id)
        .then(json => {
          if (!json.success) throw new Error(json.message)

          setIsSubmitting(false)
          setSuccessMessage(customSuccessMessage || configuration?.postSubmitAction?.value || DEFAULT_SUCCESS_MESSAGE)

          if (formRef.current && onSubmitted) onSubmitted(formRef.current)

          if (alternative_id === TRIAL_FORM_ID) {
            const trialUrl = document.querySelector<HTMLAnchorElement>("a#free-trial-link")?.href
            if (trialUrl) window.open(trialUrl, '_blank')
            return
          }

          if (redirectUrl) {
            window.open(redirectUrl, '_blank')
            return
          }
        })
    } catch (err) {
      setIsSubmitting(false)
      setErrorMessage(err.message)
    }
  }

  return (
    <div
      className={generateConditionalClassName({
        "HubspotForm": true,
        "is-email-catcher": isEmailCatcher,
        "is-compact": useMediumInputs,
      })}
    >

      <form
        className="flex"
        encType="multipart/form-data"
        method="POST"
        onSubmit={handleSubmit}
        ref={formRef}
        data-hubspot-id={alternative_id}
      >
        {legalConsentOptions?.consentToProcessText && (
          <input type="hidden" name={`legal--consentToProcess__TEXT`} value={legalConsentOptions.consentToProcessText} />
        )}
        {legalConsentOptions?.communicationsCheckboxes && legalConsentOptions?.communicationsCheckboxes.length > 1 && (
          <>
            {legalConsentOptions.communicationsCheckboxes.map(checkbox => !checkbox ? null : (
              <LegalConsentCheckbox
                {...checkbox}
                hasMultipleOptions
                isCompact={useMediumInputs}
                isDisabled={isSubmitted}
                isInvalid={inputValidationMap[`legal--${checkbox.subscriptionTypeId}`]}
                isSubscription
                key={checkbox.subscriptionTypeId}
              />
            ))}
          </>
        )}
        {fields.map((field, i) => {
          const fieldId = `${alternative_id}-${field.name}`
          const isHalfWidth =
            ["firstname", "lastname"].includes(field.name || " ") &&
            (fields[i - 1]?.name === "firstname" || fields[i + 1]?.name === "lastname")

          return (
            <div
              className={generateConditionalClassName({
                flex: true,
                "flex-item--1": !isHalfWidth,
                "flex-item--2": isHalfWidth,
                "email-field": field.fieldType === "email",
              })}
              key={`field--${field.name}`}
              style={{ display: field.hidden ? "none" : "" }}
            >
              {field.hidden && (
                <HiddenField
                  defaultValue={field.defaultValue}
                  defaultValues={field.defaultValues}
                  key={fieldId}
                  id={fieldId}
                  name={field.name || ""}
                />
              )}

              {!field.hidden && (
                <>
                  {field.fieldType === FIELD_TYPES["file"] && (
                    <FileUploadField
                      className={`flex-item--1`}
                      key={fieldId}
                      id={fieldId}
                      isCompact={useMediumInputs}
                      isDisabled={isSubmitted}
                      isInvalid={field.name ? inputValidationMap[field.name] : false}
                      field={field}
                      validateField={validateField}
                    />
                  )}

                  {/* Email / text fields */}
                  {field.fieldType && Object.keys(FIELD_TYPES).includes(field.fieldType) && (
                    <React.Fragment key={field.name}>
                      {isEmailCatcher && field.fieldType === FIELD_TYPES["email"] && (successMessage || errorMessage || isSubmitting) ? (
                        <SubmissionMessage
                          successMessage={successMessage}
                          errorMessage={errorMessage}
                          isSubmitting={isSubmitting}
                        />
                      ) : (
                        <>
                          <Field
                            className={`flex-item--1`}
                            key={fieldId}
                            id={fieldId}
                            isCompact={useMediumInputs}
                            isDisabled={isSubmitted}
                            isInvalid={field.name ? inputValidationMap[field.name] : false}
                            field={field}
                            validateField={validateField}
                          />
                          {isEmailCatcher && field.fieldType === FIELD_TYPES["email"] && (
                            <HubspotFormSubmitButton
                              buttonText={displayOptions?.submitButtonText}
                              isCompact={useMediumInputs}
                            />
                          )}
                        </>
                      )}
                    </React.Fragment>
                  )}

                  {field.richText && (
                    <div
                      className="small-text"
                      dangerouslySetInnerHTML={{ __html: xss(field.richText) }}
                    />
                  )}
                </>
              )}
            </div>
          )
        })}
        {legalConsentOptions?.communicationsCheckboxes && legalConsentOptions?.communicationsCheckboxes.length === 1 && (
          <>
            {legalConsentOptions.communicationsCheckboxes.map(checkbox => !checkbox ? null : (
              <LegalConsentCheckbox
                {...checkbox}
                isCompact={useMediumInputs}
                isDisabled={isSubmitted}
                isInvalid={inputValidationMap[`legal--${checkbox.subscriptionTypeId}`]}
                key={checkbox.subscriptionTypeId}
              />
            ))}
          </>
        )}
        {legalConsentOptions?.type === "legitimate_interest" && legalConsentOptions.subscriptionTypeIds && (
          <>
            <input
              type="hidden"
              name="legitimate_interest--subscriptionTypeId"
              value={legalConsentOptions.subscriptionTypeIds[0] || ""}
            />
            <input
              type="hidden"
              name="legitimate_interest--legalBasis"
              value={(legalConsentOptions.lawfulBasis || "").toUpperCase()}
            />
            <input
              type="hidden"
              name="legitimate_interest--text"
              value={legalConsentOptions.privacyText || ""}
            />
          </>
        )}
        {!isEmailCatcher && (
          (successMessage || errorMessage || isSubmitting) ? (
            <SubmissionMessage
              successMessage={successMessage}
              errorMessage={errorMessage}
              isSubmitting={isSubmitting}
            />
          ) : (
            <HubspotFormSubmitButton
              buttonText={displayOptions?.submitButtonText}
              isCompact={useMediumInputs}
            />
          )
        )}
        {(legalConsentOptions?.privacyText || legalConsentOptions?.consentToProcessText) && (
          <div
            className="small-text"
            dangerouslySetInnerHTML={{ __html: legalConsentOptions.privacyText || legalConsentOptions.consentToProcessText || "" }}
          />
        )}
      </form>
    </div>
  )
}

export const HubspotFormFragment = graphql`
  fragment HubspotFormInputFragment on HubspotFormFieldGroupsAlternative_fields {
    name
    placeholder
    required
    fieldType
    description
    defaultValues
    label
    hidden
    objectTypeId
    options {
      description
      displayOrder
      label
      value
    }
    validation {
      blockedEmailDomains
      useDefaultBlockList
    }
  }
  fragment HubspotFormLegalCheckboxFragment on HubspotFormLegalConsentOptionsCommunicationsCheckboxes {
    label
    required
    subscriptionTypeId
  }
  fragment HubspotFormFragment on HubspotForm {
    name
    alternative_id
    displayOptions {
      submitButtonText
    }
    configuration {
      postSubmitAction {
        type
        value
      }
    }
    fieldGroups {
      alternative_fields {
        ...HubspotFormInputFragment
      }
      groupType
      richText
      richTextType
    }
    legalConsentOptions {
      communicationsCheckboxes {
        ...HubspotFormLegalCheckboxFragment
      }
      consentToProcessText
      lawfulBasis
      privacyText
      subscriptionTypeIds
      type
    }
  }
`

export default HubspotForm
